import React, { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { API } from '../../apis/api';
import { API_RESOURCE_URLS, ASN_STATUS, ZPL_ASN_TEMPLATE, ZPL_ASN_TEMPLATE_FOR_DROPPING } from '../../constants';
import './EngineConfirmationUpdate.css';
import { useAjaxCallbackWithIntervals } from '../../hooks';
import {
    getFormattedAsn,
    splitDescription,
    compareByDateAndSerialNumber,
} from '../../utils/tableUtils';
import { usePopupManager } from '../../providers/PopupManager/PopupManager';
import { buildErrorMessage } from '../../apis/calls';
import YellowArrowIcon from '../../assets/icons/yellow_arrow.svg';

const EngineConfirmationTable = ({ plant, line, isSplitView }) => {


    const latestLineValueRef = useRef(line?.value);
    const latestPlantValueRef = useRef(plant?.value);

    const { showInternalError, showError } = usePopupManager();

    const [droppedRowsData, setDroppedRowsData] = useState({
        data: [],
        loading: false,
    });

    const [readyForDropRowsData, setReadyForDropRowsData] = useState({
        data: [],
        loading: false,
    });


    // Inline Form State
    const [expandedRow, setExpandedRow] = useState({ index: null, type: null });
    const [engineMaterialNumber, setengineMaterialNumber] = useState('');
    const [selectedRowForEngine, setSelectedRowForEngine] = useState(null);
    const [printStatusData, setPrintStatusData] = useState({});

    const [allDroppingUpdates, setAllDroppingUpdates] = useState({});

    const fetchAllDroppingUpdates = async () => {
        if (!plant?.value) return;
        try {
            const response = await API.get(API_RESOURCE_URLS.getAllDroppingUpdate(plant.value));
            const updatesMap = {};
            if (response.data && Array.isArray(response.data)) {
                response.data.forEach(item => {
                    if (item.asnId) {
                        updatesMap[item.asnId] = item;
                    }
                });
            }
            setAllDroppingUpdates(updatesMap);
        } catch (error) {
            showError({ error });
            console.error('Failed to fetch all dropping updates', error);
        }
    };

    const fetchPrintStatus = async (asnIds) => {
        if (!plant?.value || !asnIds || asnIds.length === 0) return;
        try {
            const response = await API.get(API_RESOURCE_URLS.getStatusPrintDispatch(plant.value, asnIds.join(',')));
            const statusMap = {};
            if (response.data && Array.isArray(response.data)) {
                response.data.forEach(item => {
                    if (item.asn) {
                        statusMap[item.asn] = item;
                    }
                });
            }
            setPrintStatusData(statusMap);
        } catch (error) {
            console.error('Failed to fetch print status', error);
            showError({ error });
        }
    };

    const handleEngineClick = (row, index, type) => {
        if (expandedRow.index === index && expandedRow.type === type) {
            // Toggle off if already open
            handleCloseInlineForm();
        } else {
            setExpandedRow({ index, type });
            setSelectedRowForEngine(row);
            setengineMaterialNumber(''); // Reset input
        }
    };

    const handleCloseInlineForm = () => {
        setExpandedRow({ index: null, type: null });
        setSelectedRowForEngine(null);
        setengineMaterialNumber('');
    };

    const handleSubmitInlineForm = async () => {
        if (!selectedRowForEngine || !plant?.value) return;

        const payload = {
            asn: selectedRowForEngine.asn,
            materialNumber: selectedRowForEngine.materialNumber,
            materialDescription: selectedRowForEngine.materialDescription,
            status: ASN_STATUS.DROPPED,
            asnId: selectedRowForEngine.asnId,
        };

        try {

            await API.put(API_RESOURCE_URLS.droppingUpdate(plant.value), payload);
            handleCloseInlineForm();
            // Refresh data
            loadRows();
        } catch (error) {
            console.error('Failed to submit Engine - Dispatch confirmation', error);
            showInternalError({ error });
        }
    };

    const handlePrintClick = async (row) => {
        if (!row) return;
        const asn = row.asn;
        const vc = row.materialNumber;
        const description = row.vcAlias;

        const updateInfo = row && row.asn ? allDroppingUpdates[row.asn] : null;
        const engineMaterialNumber = row.engineMaterialNumber || (updateInfo ? updateInfo.engineMaterialNumber : '');
        const engineMaterialDescription = row.engineMaterialDescription || (updateInfo ? updateInfo.engineMaterialDescription : '');

        if (asn) {
            try {
                await API.put(API_RESOURCE_URLS.updateStatusPrintDispatch(plant.value), {
                    asn: asn,
                    printStatus: "PRINT",
                    dispatchStatus: ""
                });
                loadRows();
            } catch (error) {
                showError({ error });
                console.error('Failed to update print status', error);
            }
        }

        // let printAsn = asn;
        // if (printAsn && printAsn.split('-').length > 3) {
        //     printAsn = printAsn.split('-').slice(0, 3).join('-');
        // }

        const label = ZPL_ASN_TEMPLATE_FOR_DROPPING
            .replaceAll("{LineID}", line?.label || '')
            .replaceAll("{ASN}", asn || '')
            .replaceAll("{MaterialNumber}", vc || '')
            .replaceAll("{MaterialDescription}", description || '')
            .replaceAll("{EngineMaterialDescription}", engineMaterialDescription || '')
            .replaceAll("{EngineMaterialNumber}", engineMaterialNumber || '');

        const zplstring = encodeURIComponent(label);

        window.location.href = `nirmaan://?cmd=print&zplstring=${zplstring}`;
    };

    const getAsnsByStatusForCount = async (status, count, optionalParams) => {
        if (plant?.value == null) return;
        if (line?.value == null) return;

        const response = await API.get(
            API_RESOURCE_URLS.getDroppingUpdate(
                plant.value,
                line.value,
                status,
                count,
            ),
        );

        return response.data;
    };

    const prepareLastDroppedAsnRows = (asns) => {
        let sortDateByDesc =
            asns &&
            asns.sort((a, b) => {
                if (
                    a.dropTimestamp != null &&
                    a !== undefined &&
                    b.dropTimestamp != null &&
                    b !== undefined
                )
                    return (
                        new Date(...b?.dropTimestamp.split('/').reverse()) -
                        new Date(...a?.dropTimestamp.split('/').reverse())
                    );
                else return [];
            });

        let position = 0;
        const addPositionValue = sortDateByDesc?.map(FormatedValue => {
            position = position + 1;
            return { ...FormatedValue, position: position };
        });

        return addPositionValue || [];
    };

    const loadLastDroppedAsn = async () => {
        setDroppedRowsData(prevState => ({ ...prevState, loading: true }));
        try {
            const noOfRows = 60;

            const asns = await getAsnsByStatusForCount(ASN_STATUS.DROPPED, noOfRows);

            if (
                plant?.value !== latestPlantValueRef.current ||
                line?.value !== latestLineValueRef.current
            ) {
                setDroppedRowsData(prevState => ({
                    ...prevState,
                    loading: false,
                }));
                return [];
            }

            const validAsns = Array.isArray(asns) ? asns : [];

            const positionallyFormattedRows = prepareLastDroppedAsnRows(
                validAsns,
            );

            setDroppedRowsData({
                data: positionallyFormattedRows,
                loading: false,
            });
            return positionallyFormattedRows;
        } catch (err) {
            console.error(
                'failed to load last dropped asn, err:' + buildErrorMessage(err),
            );
            setDroppedRowsData({
                data: [],
                loading: false,
            });
            showInternalError();
            return [];
        }
    };

    const prepareReadyForDropRows = (data) => {
        return data ? data.sort((a, b) => b.position - a.position) : [];
    };

    const loadFirstReadyForDroppedAsn = async () => {
        setReadyForDropRowsData(prevState => ({ ...prevState, loading: true }));
        try {
            const noOfRows = 60;
            const asns = await getAsnsByStatusForCount(
                ASN_STATUS.READY_FOR_DROP,
                noOfRows,
            );
            const validAsns = Array.isArray(asns) ? asns : [];
            const noOfRowsForAsn = validAsns.length;

            if (
                plant?.value !== latestPlantValueRef.current ||
                line?.value !== latestLineValueRef.current
            ) {
                setReadyForDropRowsData(prevState => ({
                    ...prevState,
                    loading: false,
                }));
                return [];
            }

            const sortedData = asns?.sort((a, b) =>
                compareByDateAndSerialNumber(a, b, 'productionDate', 'sequence'),
            );

            let position = 0;
            const addPositionValue = sortedData?.map(FormatedValue => {
                position = position + 1;
                return { ...FormatedValue, position: position };
            });

            const positionallyFormattedRows = prepareReadyForDropRows(
                addPositionValue,
            );
            setReadyForDropRowsData({
                data: positionallyFormattedRows,
                loading: false,
            });
            return positionallyFormattedRows;
        } catch (err) {
            console.error(
                'failed to load last ready for drop asn, err:' + buildErrorMessage(err),
            );
            setReadyForDropRowsData({
                data: [],
                loading: false,
            });
            showInternalError();
            return [];
        }
    };

    const loadRows = async () => {
        const [readyRows, droppedRows] = await Promise.all([
            loadFirstReadyForDroppedAsn(),
            loadLastDroppedAsn(),
            fetchAllDroppingUpdates(),
        ]);

        const readyAsns = readyRows ? readyRows.map(r => r.asn) : [];
        const droppedAsns = droppedRows ? droppedRows.map(r => r.asn) : [];
        const allAsns = [...readyAsns, ...droppedAsns].filter(Boolean);

        if (allAsns.length > 0) {
            await fetchPrintStatus(allAsns);
        }
    };

    useEffect(() => {
        latestLineValueRef.current = line?.value;
        latestPlantValueRef.current = plant?.value;
    }, [line.value, plant.value]);

    const lastRefreshedDate = useAjaxCallbackWithIntervals(
        droppedRowsData.loading || readyForDropRowsData.loading,
        false,
        loadRows,
        line?.value,
        plant?.value,
    );

    const getPaddedPositionString = positionIndex =>
        String(positionIndex).padStart(2, '0');

    const renderIndicators = (index, isActiveEngine, isPrinted, isDroppedSection) => {
        if (isDroppedSection && index === 25) {
            return (
                <div className="indicator-arrow-container">
                    <img src={YellowArrowIcon} alt="Current Station" className="indicator-arrow-icon" />
                </div>
            );
        }

        return (
            <div className="indicator-container">
                {isActiveEngine && (
                    <div className="indicator-dot engine-confirmed" title="Engine Confirmed"></div>
                )}
                {isPrinted && (
                    <div className="indicator-dot printed" title="Printed"></div>
                )}
                {!isActiveEngine && !isPrinted && isDroppedSection && index === 0 && 'Station'}
                {!isActiveEngine && !isPrinted && !isDroppedSection && index === readyForDropRowsData.data.length - 1 && 'LineUp'}
            </div>
        );
    };

    const renderRow = index => {
        const row = droppedRowsData.data[index];

        const [descriptionHead, descriptionTail] = splitDescription(
            row && row.vcAlias,
        );

        const position = row ? row.position : index + 1;

        const isExpanded = expandedRow.index === index && expandedRow.type === 'DROPPED';

        const updateInfo = row && row.asn ? allDroppingUpdates[row.asn] : null;
        const isActiveEngine = updateInfo ? updateInfo.activeEngine : false;
        const printInfo = row && row.asn ? printStatusData[row.asn] : null;
        const isPrinted = printInfo && printInfo.printStatus === 'PRINT';

        const engineMaterialNumber = row && (row.engineMaterialNumber || (updateInfo && updateInfo.engineMaterialNumber)) ? row.engineMaterialNumber || updateInfo.engineMaterialNumber : '-';
        const engineMaterialDescription = row && (row.engineMaterialDescription || (updateInfo && updateInfo.engineMaterialDescription)) ? row.engineMaterialDescription || updateInfo.engineMaterialDescription : '-';
        const isDispatched = updateInfo && updateInfo.dispatchStatus === 'DISPATCH';
        const printCount = printInfo && printInfo.printCount ? printInfo.printCount : 0;

        return (
            <React.Fragment key={index}>
                <tr key={index}>
                    <td width="2%" height="30" align="center" valign="middle">
                        {renderIndicators(index, isActiveEngine, isPrinted, true)}
                    </td>
                    <td width="6%" align="center" valign="middle">
                        <button className="action-btn engine" onClick={() => handleEngineClick(row, index, 'DROPPED')} disabled={isDispatched} style={{ opacity: isDispatched ? 0.5 : 1, cursor: isDispatched ? 'not-allowed' : 'pointer' }}>Dispatch</button>
                        <button
                            className="action-btn print"
                            onClick={() => handlePrintClick(row)}
                            disabled={!row || !row.asn}
                            style={{ opacity: (!row || !row.asn) ? 0.5 : 1, cursor: (!row || !row.asn) ? 'not-allowed' : 'pointer' }}
                        >
                            Print
                        </button>
                    </td>
                    <td width="2%" align="center" valign="middle">
                        {getPaddedPositionString(position)}
                    </td>
                    <td
                        width="5%"
                        align="center"
                        valign="middle"
                        className="asn"
                        data-testid={`select-Dropped-asn-${index}`}
                    >
                        {getFormattedAsn(row)}
                    </td>
                    <td
                        align="left"
                        valign="middle"
                        width="16%"
                        style={{ paddingLeft: '11px' }}
                    >
                        <span className="blue">{descriptionHead || '-'}</span>{' '}
                        {descriptionTail || '-'}
                    </td>
                    <td width="9%">
                        {row && row.materialNumber ? row.materialNumber : '-'}
                    </td>
                    <td width="7%">
                        {engineMaterialNumber}
                    </td>
                    <td width="14%">
                        {engineMaterialDescription}
                    </td>
                    <td width="7%">
                        {row && row.gearboxPart ? row.gearboxPart : '-'}
                    </td>
                    <td width="7%">
                        {row && row.loadBodyType ? row.loadBodyType : '-'}
                    </td>
                    <td width="11%">
                        {row && row.serialNumber ? row.serialNumber : '-'}
                    </td>
                    <td width="14%">
                        {row && row.dropTimestamp ? moment(row.dropTimestamp).format('hh:mm:ss A DD/MM/YYYY') : '-'}
                    </td>
                </tr>
                {isExpanded && (
                    <tr className="inline-form-row">
                        <td colSpan="12">
                            <div className="inline-form-container">
                                <div className="inline-form-arrow"></div>
                                <div className="inline-input-group">
                                    <span className="inline-label" style={{ fontSize: '14px', color: '#333' }}>Engine - Dispatch confirmation</span>
                                </div>
                                <div className="inline-actions">
                                    <button className="inline-btn submit" onClick={handleSubmitInlineForm}>Submit</button>
                                    <button className="inline-btn cancel" onClick={handleCloseInlineForm}>Cancel</button>
                                </div>
                            </div>
                        </td>
                    </tr>
                )}
            </React.Fragment>
        );
    };

    const renderReadyForDropRow = index => {
        const row = readyForDropRowsData.data[index];
        const [descriptionHead, descriptionTail] = splitDescription(
            row && row.vcAlias,
        );

        const position = row.position;

        const conditionalBorderClass =
            index + 1 > 0 && (index + 1) % 5 === 0 && 'borderBt';

        const isExpanded = expandedRow.index === index && expandedRow.type === 'READY';

        const updateInfo = row && row.asn ? allDroppingUpdates[row.asn] : null;
        const isActiveEngine = updateInfo ? updateInfo.activeEngine : false;
        const printInfo = row && row.asn ? printStatusData[row.asn] : null;
        const isPrinted = printInfo && printInfo.printStatus === 'PRINT';

        const engineMaterialNumber = row && (row.engineMaterialNumber || (updateInfo && updateInfo.engineMaterialNumber)) ? row.engineMaterialNumber || updateInfo.engineMaterialNumber : '-';
        const engineMaterialDescription = row && (row.engineMaterialDescription || (updateInfo && updateInfo.engineMaterialDescription)) ? row.engineMaterialDescription || updateInfo.engineMaterialDescription : '-';
        const isDispatched = updateInfo && updateInfo.dispatchStatus === 'DISPATCH';
        const printCount = printInfo && printInfo.printCount ? printInfo.printCount : 0;

        return (
            <React.Fragment key={index}>
                <tr className="borderBt" key={index} data-testid={`select-row-${index}`}>
                    <td
                        className={conditionalBorderClass}
                        width="2%"
                        height="30"
                        align="center"
                        valign="middle"
                    >
                        {renderIndicators(index, isActiveEngine, isPrinted, false)}
                    </td>
                    <td
                        className={conditionalBorderClass}
                        width="6%"
                        align="center"
                        valign="middle"
                    >
                        <button className="action-btn engine" onClick={() => handleEngineClick(row, index, 'READY')} disabled={isDispatched} style={{ opacity: isDispatched ? 0.5 : 1, cursor: isDispatched ? 'not-allowed' : 'pointer' }}>Dispatch</button>
                        <button
                            className="action-btn print"
                            onClick={() => handlePrintClick(row)}
                            disabled={!row || !row.asn}
                            style={{ opacity: (!row || !row.asn) ? 0.5 : 1, cursor: (!row || !row.asn) ? 'not-allowed' : 'pointer' }}
                        >
                            Print
                        </button>
                    </td>
                    <td
                        className={conditionalBorderClass}
                        width="2%"
                        height="30"
                        align="center"
                        valign="middle"
                    >
                        {getPaddedPositionString(position)}
                    </td>
                    <td
                        className={conditionalBorderClass}
                        width="5%"
                        height="30"
                        align="center"
                        valign="middle"
                        data-testid={`select-readyForDrop-asn-${index}`}
                    >
                        {getFormattedAsn(row)}
                    </td>
                    <td
                        className={conditionalBorderClass}
                        width="16%"
                        align="left"
                        valign="middle"
                        style={{ paddingLeft: '11px' }}
                    >
                        <span className="blue">{descriptionHead || '-'}</span>{' '}
                        {descriptionTail || '-'}
                    </td>
                    <td
                        className={conditionalBorderClass}
                        width="9%"
                        align="left"
                        valign="middle"
                    >
                        {row && row.materialNumber ? row.materialNumber : '-'}
                    </td>
                    <td
                        className={conditionalBorderClass}
                        width="7%"
                        align="left"
                        valign="middle"
                    >
                        {engineMaterialNumber}
                    </td>
                    <td
                        className={conditionalBorderClass}
                        width="14%"
                        align="left"
                        valign="middle"
                    >
                        {engineMaterialDescription}
                    </td>
                    <td
                        className={conditionalBorderClass}
                        width="7%"
                        align="left"
                        valign="middle"
                    >
                        {row && row.gearboxPart ? row.gearboxPart : '-'}
                    </td>
                    <td
                        className={conditionalBorderClass}
                        width="7%"
                        align="left"
                        valign="middle"
                    >
                        {row && row.loadBodyType ? row.loadBodyType : '-'}
                    </td>
                    <td
                        className={conditionalBorderClass}
                        width="11%"
                        align="left"
                        valign="middle"
                    >
                        {row && row.serialNumber ? row.serialNumber : '-'}
                    </td>
                    <td
                        className={conditionalBorderClass}
                        width="14%"
                        align="left"
                        valign="middle"
                    >
                        {row && row.dropTimestamp ? moment(row.dropTimestamp).format('hh:mm:ss A DD/MM/YYYY') : '-'}
                    </td>
                </tr>
                {isExpanded && (
                    <tr className="inline-form-row">
                        <td colSpan="12">
                            <div className="inline-form-container">
                                <div className="inline-form-arrow"></div>
                                <div className="inline-input-group">
                                    <span className="inline-label" style={{ fontSize: '14px', color: '#333' }}>Engine - Dispatch confirmation</span>
                                </div>
                                <div className="inline-actions">
                                    <button className="inline-btn submit" onClick={handleSubmitInlineForm}>Submit</button>
                                    <button className="inline-btn cancel" onClick={handleCloseInlineForm}>Cancel</button>
                                </div>
                            </div>
                        </td>
                    </tr>
                )}
            </React.Fragment>
        );
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', width: '100%', minHeight: 'calc(100vh - 120px)' }}>
            {/* Top Section: Ready For Drop */}
            <div style={{ display: 'flex', flexDirection: 'column', borderBottom: '2px solid #4d5053' }}>
                <div className="droprow" style={{ padding: '5px 10px' }}>
                    Ready For Drop {line?.label ? `(${line.label})` : ''}
                </div>
                <table width="100%" align="left" style={{ tableLayout: 'fixed' }}>
                    <tr className="asnHeading">
                        <td width="2%" align="left" valign="middle">&nbsp;</td>
                        <td width="6%" align="center" valign="middle">Action</td>
                        <td width="2%" align="left" valign="middle">&nbsp;</td>
                        <td width="5%" height="30" align="center" valign="middle">ASN</td>
                        <td width="16%" align="left" valign="middle" style={{ paddingLeft: '11px' }}>VC Description</td>
                        <td width="9%" align="left" valign="middle">VC</td>
                        <td width="7%" align="left" valign="middle">Engine Part Number</td>
                        <td width="14%" align="left" valign="middle">Engine Part Description</td>
                        <td width="7%" align="left" valign="middle">Gearbox Part</td>
                        <td width="7%" align="left" valign="middle">Load Body Type</td>
                        <td width="11%" align="left" valign="middle">Chassis Number</td>
                        <td width="14%" align="left" valign="middle">Drop Time Stamp</td>
                    </tr>
                </table>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <table width="100%" border="0" cellSpacing="0" cellPadding="0" className="inner-table" data-testid={`select-row-table`} style={{ marginTop: 'auto', tableLayout: 'fixed' }}>
                        {readyForDropRowsData.data.map((_, index) => renderReadyForDropRow(index))}
                    </table>
                </div>
            </div>

            {/* Bottom Section: Dropped Chassis */}
            <div style={{ display: 'flex', flexDirection: 'column' }}>
                <div className="droprow" style={{ color: '#e8f1ed', background: '#01653c', padding: '5px 10px' }}>
                    DROPPED CHASSIS
                </div>
                <div>
                    <table width="100%" border="0" cellSpacing="0" cellPadding="0" className="inner-table" style={{ tableLayout: 'fixed' }}>
                        {droppedRowsData.data.map((_, index) => renderRow(index))}
                    </table>
                </div>
            </div>

            {/* Footer */}
            <div style={{ marginTop: 'auto', height: '30px', textAlign: 'center', fontSize: '0.8em', borderTop: 'solid 1px #4d5053', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                Last Refreshed: {lastRefreshedDate ? lastRefreshedDate.format('hh:mmA [(IST)] DD MMMM YYYY') : '--:--'}
            </div>
        </div>
    );
};

export default EngineConfirmationTable;
